import { Loader2Icon, MoreVerticalIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useVidyoStore } from "@/features/vidyo-call/utils/vidyo-store";

import {
  useMuteParticipantAudio,
  useUnmuteParticipantAudio,
} from "../api/mute-participant-audio";
import {
  useMuteParticipantVideo,
  useUnmuteParticipantVideo,
} from "../api/mute-participant-video";
import { useRemoveParticipant } from "../api/remove-participant";
import { Participant } from "../types/participant";

type ParticipantItemActionProps = {
  participant: Participant;
};

export function ParticipantItemAction({
  participant,
}: ParticipantItemActionProps) {
  const sessionId = useVidyoStore((state) => state.sessionId);

  const { mutateAsync: removeParticipant, isPending: isRemovingParticipant } =
    useRemoveParticipant();
  const { mutateAsync: muteParticipantAudio, isPending: isMutatingAudio } =
    useMuteParticipantAudio();
  const { mutateAsync: muteParticipantVideo, isPending: isMutatingVideo } =
    useMuteParticipantVideo();
  const { mutateAsync: unmuteParticipantAudio, isPending: isUnmutingAudio } =
    useUnmuteParticipantAudio();
  const { mutateAsync: unmuteParticipantVideo, isPending: isUnmutingVideo } =
    useUnmuteParticipantVideo();

  const isLoading =
    isRemovingParticipant ||
    isMutatingAudio ||
    isMutatingVideo ||
    isUnmutingAudio ||
    isUnmutingVideo;

  const handleRemoveParticipant = async () => {
    await removeParticipant({
      sessionId,
      body: {
        participantId: participant.participantId,
        entityId: participant.entityId,
      },
    });
  };

  const handleMuteParticipantAudio = async () => {
    await muteParticipantAudio({
      sessionId,
      body: {
        participantId: participant.participantId,
        entityId: participant.entityId,
      },
    });
  };

  const handleUnmuteParticipantAudio = async () => {
    await unmuteParticipantAudio({
      sessionId,
      body: {
        participantId: participant.participantId,
        entityId: participant.entityId,
      },
    });
  };

  const handleMuteParticipantVideo = async () => {
    await muteParticipantVideo({
      sessionId,
      body: {
        participantId: participant.participantId,
        entityId: participant.entityId,
      },
    });
  };

  const handleUnmuteParticipantVideo = async () => {
    await unmuteParticipantVideo({
      sessionId,
      body: {
        participantId: participant.participantId,
        entityId: participant.entityId,
      },
    });
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" disabled={isLoading}>
          {isLoading ? (
            <Loader2Icon className="size-4 animate-spin" />
          ) : (
            <MoreVerticalIcon className="size-4" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onClick={
            participant.isVideoEnabled
              ? handleMuteParticipantVideo
              : handleUnmuteParticipantVideo
          }
        >
          {participant.isVideoEnabled ? "Disable Video" : "Enable Video"}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={
            participant.isAudioEnabled
              ? handleMuteParticipantAudio
              : handleUnmuteParticipantAudio
          }
        >
          {participant.isAudioEnabled ? "Disable Audio" : "Enable Audio"}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleRemoveParticipant}>
          Remove
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
